import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useThemeMode } from '@/contexts/ThemeContext'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { signOut } from '@/lib/auth'
import { clearGuestSession } from '@/lib/guestStorage'
import Button from '@/components/ui/Button'
import AccountInfo from '@/components/AccountInfo'
import Subscription from '@/components/Subscription'
import ImportChatGPT from '@/components/ImportChatGPT'
import AuthWindow from '@/components/AuthWindow'

interface WindowTab {
  id: number
  title: string
  contentType?: string
  isActive: boolean
  isMinimized?: boolean
}

interface MobileNavProps {
  windows: WindowTab[]
  activeWindowIndex: number
  onWindowSelect: (index: number) => void
  onOpenWindow: (type: string) => void
  onCloseWindow: (id: number) => void
  onThemeToggle: () => void
  isGuestMode?: boolean
  onLoginClick?: () => void
  showLoginModal?: boolean
  onLoginSuccess?: () => void
  onCloseLoginModal?: () => void
  fontMode: 'departure-mono' | 'open-dyslexic' | 'system'
}

export default function MobileNav({
  windows,
  activeWindowIndex,
  onWindowSelect,
  onOpenWindow,
  onCloseWindow,
  onThemeToggle,
  isGuestMode = false,
  onLoginClick,
  showLoginModal = false,
  onLoginSuccess,
  onCloseLoginModal,
  fontMode,
}: MobileNavProps) {
  const { isDark, colors } = useThemeMode()
  const { t } = useTranslation()
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  
  // Long-press context menu state
  const [contextMenu, setContextMenu] = useState<{ type: string; x: number; y: number } | null>(null)
  const longPressTimerRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  
  // App switcher state
  const [showAppSwitcher, setShowAppSwitcher] = useState(false)
  const swipeStartRef = useRef<{ y: number; time: number } | null>(null)
  const bottomNavRef = useRef<HTMLDivElement>(null)
  
  // Modal states
  const [showAccountModal, setShowAccountModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showImportChatGPTModal, setShowImportChatGPTModal] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const navItems = [
    { type: 'chat', icon: normalizeEmojis('💬'), label: t('navigation.chat') },
    { type: 'fileExplorer', icon: normalizeEmojis('📁'), label: t('navigation.fileExplorer') },
    { type: 'settings', icon: normalizeEmojis('⚙️'), label: t('navigation.settings') },
    { type: 'profile', icon: normalizeEmojis('👤'), label: t('navigation.profile') },
    { type: 'representation', icon: normalizeEmojis('🧠'), label: t('navigation.representation') },
  ]

  // Close menus when clicking/tapping outside
  useEffect(() => {
    if (!showMenu) return
    
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [showMenu])
  
  // Close app switcher when all windows are closed
  useEffect(() => {
    if (showAppSwitcher && windows.length === 0) {
      setShowAppSwitcher(false)
    }
  }, [showAppSwitcher, windows.length])
  
  // Close context menu when tapping elsewhere
  useEffect(() => {
    if (!contextMenu) return
    const handleTap = () => setContextMenu(null)
    document.addEventListener('touchstart', handleTap)
    document.addEventListener('mousedown', handleTap)
    return () => {
      document.removeEventListener('touchstart', handleTap)
      document.removeEventListener('mousedown', handleTap)
    }
  }, [contextMenu])
  
  // Long press handlers
  const handleLongPressStart = useCallback((type: string, e: React.TouchEvent | React.MouseEvent) => {
    longPressTriggeredRef.current = false
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true
      setContextMenu({
        type,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      })
    }, 500)
  }, [])
  
  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }, [])
  
  // Swipe up detection for app switcher
  const handleNavTouchStart = useCallback((e: React.TouchEvent) => {
    swipeStartRef.current = {
      y: e.touches[0].clientY,
      time: Date.now(),
    }
  }, [])
  
  const handleNavTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!swipeStartRef.current) return
    const deltaY = swipeStartRef.current.y - e.changedTouches[0].clientY
    const deltaTime = Date.now() - swipeStartRef.current.time
    // Swipe up detected (at least 50px up, within 300ms)
    if (deltaY > 50 && deltaTime < 300 && windows.length > 0) {
      setShowAppSwitcher(true)
    }
    swipeStartRef.current = null
  }, [windows.length])

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      const result = await signOut()
      if (result.error) {
        console.error('Logout error:', result.error)
        setIsLoggingOut(false)
        return
      }
      clearGuestSession()
      window.location.reload()
    } catch (error) {
      console.error('Logout error:', error)
      setIsLoggingOut(false)
    }
  }

  const fontFamily =
    fontMode === 'departure-mono'
      ? 'var(--font-departure-mono), monospace'
      : fontMode === 'open-dyslexic'
        ? 'var(--font-open-dyslexic), sans-serif'
        : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'

  return (
    <>
      {/* Top Bar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 'calc(44px + env(safe-area-inset-top))',
          backgroundColor: colors.navBg,
          borderBottom: `1px solid ${colors.navBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px',
          paddingTop: 'env(safe-area-inset-top)',
          zIndex: 1000,
          // Removed backdrop-filter for better mobile performance
          fontFamily,
        }}
      >
        {/* Left: Logo + Window Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <a
            href="https://honcho.chat"
            aria-label="Honcho Chat"
            style={{
              color: colors.visualAccent1,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="20" height="18" viewBox="0 0 87 77" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M55.29 0H38.77V6.89H55.29V0Z" fill="currentColor"/>
              <path d="M65.95 6.89H55.29V13.69H65.95V6.89Z" fill="currentColor"/>
              <path d="M72.66 13.69H65.95V20.4H72.66V13.69Z" fill="currentColor"/>
              <path d="M79.54 20.4H72.66V30.17H79.54V20.4Z" fill="currentColor"/>
              <path d="M86.33 30.16H79.54V46.54H86.33V30.16Z" fill="currentColor"/>
              <path d="M79.54 46.55H72.67V56.32H79.54V46.55Z" fill="currentColor"/>
              <path d="M72.67 56.31H65.95V63.11H72.67V56.31Z" fill="currentColor"/>
              <path d="M65.95 63.11H55.29V69.96H65.95V63.11Z" fill="currentColor"/>
              <path d="M55.29 69.96H35.84V76.65H55.29V69.96Z" fill="currentColor"/>
              <path d="M35.84 63.11H25.18V69.96H35.84V63.11Z" fill="currentColor"/>
              <path d="M25.18 56.31H18.39V63.11H25.18V56.31Z" fill="currentColor"/>
              <path d="M18.39 46.55H11.52V56.32H18.39V46.55Z" fill="currentColor"/>
              <path d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z" fill="currentColor"/>
              <path d="M18.31 27.17H11.52V33.96H18.31V27.17Z" fill="currentColor"/>
              <path d="M38.77 6.89H18.39V13.69H38.77V6.89Z" fill="currentColor"/>
              <path d="M45.5 13.69H38.79V20.4H45.5V13.69Z" fill="currentColor"/>
              <path d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z" fill="currentColor"/>
              <path d="M38.79 30.16H31.98V36.87H38.79V30.16Z" fill="currentColor"/>
              <path d="M59.09 30.16H52.28V36.87H59.09V30.16Z" fill="currentColor"/>
              <path d="M59.16 50.47H31.98V56.31H59.16V50.47Z" fill="currentColor"/>
            </svg>
          </a>

          {/* Window Manager Button - tap to open app switcher */}
          {windows.length > 0 && (
            <button
              onClick={() => {
                setShowAppSwitcher(true)
                setShowMenu(false)
              }}
              aria-label="Open app switcher"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '6px',
                backgroundColor: showAppSwitcher ? colors.primaryAccent : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'),
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: showAppSwitcher ? colors.primaryBg : colors.primaryText,
                transition: 'all 0.15s ease',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
              </svg>
            </button>
          )}
        </div>

        {/* Center: Window Title with marquee for long titles */}
        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            padding: '0 12px',
            position: 'relative',
          }}
        >
          <style>{`
            @keyframes marquee {
              0% { transform: translateX(0%); }
              10% { transform: translateX(0%); }
              45% { transform: translateX(calc(-100% + 100px)); }
              55% { transform: translateX(calc(-100% + 100px)); }
              90% { transform: translateX(0%); }
              100% { transform: translateX(0%); }
            }
            .marquee-text {
              display: inline-block;
              white-space: nowrap;
            }
            .marquee-text.animate {
              animation: marquee 8s ease-in-out infinite;
            }
          `}</style>
          <div
            style={{
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: '600',
              color: colors.primaryText,
              letterSpacing: '0.5px',
              overflow: 'hidden',
            }}
          >
            <span
              className={`marquee-text ${(windows[activeWindowIndex]?.title?.length || 0) > 25 ? 'animate' : ''}`}
              style={{
                display: 'inline-block',
                whiteSpace: 'nowrap',
              }}
            >
              {windows[activeWindowIndex]?.title || 'Honcho Chat'}
            </span>
          </div>
        </div>

        {/* Right Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Login button for guests */}
          {isGuestMode && onLoginClick && (
            <button
              onClick={onLoginClick}
              aria-label="Login"
              style={{
                padding: '6px 10px',
                backgroundColor: colors.visualAccent1,
                color: '#ffffff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              LOGIN
            </button>
          )}

          {/* Theme toggle */}
          <button
            onClick={onThemeToggle}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              padding: '6px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ 
              fontFamily: "'Noto Emoji', sans-serif",
              fontWeight: 600,
              WebkitTextStroke: '0.5px currentColor',
            }}>{isDark ? normalizeEmojis('🌙') : normalizeEmojis('☀️')}</span>
          </button>

          {/* Menu button - only show when logged in */}
          {!isGuestMode && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => {
                setShowMenu(!showMenu)
                setContextMenu(null)
              }}
              style={{
                padding: '6px',
                background: showMenu ? colors.secondaryBg : 'transparent',
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div style={{ width: '14px', height: '2px', backgroundColor: colors.primaryText, borderRadius: '1px' }} />
              <div style={{ width: '14px', height: '2px', backgroundColor: colors.primaryText, borderRadius: '1px' }} />
              <div style={{ width: '14px', height: '2px', backgroundColor: colors.primaryText, borderRadius: '1px' }} />
            </button>

            {/* Dropdown menu - only rendered for logged-in users */}
            {showMenu && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 2px)',
                right: 0,
                backgroundColor: colors.primaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: '10px',
                boxShadow: isDark
                  ? '0 8px 24px rgba(0, 0, 0, 0.6)'
                  : '0 8px 24px rgba(0, 0, 0, 0.2)',
                minWidth: '180px',
                maxHeight: '70vh',
                overflowY: 'auto',
                zIndex: 99999,
              }}
            >
              <button
                    onClick={() => {
                      setShowMenu(false)
                      setShowAccountModal(true)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: colors.primaryText,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('👤')}</span> {t('menu.account')}
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      setShowSubscriptionModal(true)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: colors.primaryText,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('💳')}</span> {t('menu.subscription')}
                  </button>
                  <div style={{ height: '1px', backgroundColor: colors.primaryBorder, margin: '4px 0' }} />
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      setShowLogoutConfirm(true)
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🚪')}</span> {t('menu.logOut')}
              </button>
            </div>
          )}
          </div>
          )}
        </div>
      </div>

      {/* Bottom Tab Bar - swipe up to open app switcher */}
      <div
        ref={bottomNavRef}
        onTouchStart={handleNavTouchStart}
        onTouchEnd={handleNavTouchEnd}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 'calc(60px + env(safe-area-inset-bottom))',
          paddingBottom: 'env(safe-area-inset-bottom)',
          backgroundColor: colors.navBg,
          borderTop: `1px solid ${colors.navBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          paddingTop: '6px',
          paddingLeft: '4px',
          paddingRight: '4px',
          zIndex: 1000,
          // Removed backdrop-filter for better mobile performance
        }}
      >
        {navItems.map((item) => {
          const windowForType = windows.find(w => w.contentType === item.type)
          const windowIndex = windows.findIndex(w => w.contentType === item.type)
          const isOpen = windowIndex !== -1
          const isActive = windowIndex === activeWindowIndex
          
          return (
            <div
              key={item.type}
              style={{
                flex: 1,
                maxWidth: '72px',
                position: 'relative',
              }}
            >
              <button
                onClick={() => {
                  // Don't trigger click if long press was triggered
                  if (longPressTriggeredRef.current) {
                    longPressTriggeredRef.current = false
                    return
                  }
                  if (isOpen) {
                    onWindowSelect(windowIndex)
                  } else {
                    onOpenWindow(item.type)
                  }
                }}
                onTouchStart={(e) => handleLongPressStart(item.type, e)}
                onTouchEnd={handleLongPressEnd}
                onTouchMove={handleLongPressEnd}
                onMouseDown={(e) => handleLongPressStart(item.type, e)}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  padding: '4px 2px',
                  background: isActive ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)') : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  opacity: isOpen ? 1 : 0.35,
                  transition: 'all 0.15s ease',
                }}
              >
                <span
                  style={{
                    fontSize: '24px',
                    lineHeight: 1,
                    position: 'relative',
                    fontFamily: "'Noto Emoji', sans-serif",
                    fontWeight: 600,
                    WebkitTextStroke: '0.5px currentColor',
                  }}
                >
                  {item.icon}
                </span>
                {/* Active indicator line instead of dot */}
                <div
                  style={{
                    width: isActive ? '16px' : '0px',
                    height: '2px',
                    borderRadius: '1px',
                    backgroundColor: colors.visualAccent1,
                    transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              </button>
            </div>
          )
        })}
      </div>
      
      {/* Long-press Context Menu - Pure CSS animation */}
      {contextMenu && createPortal(
        <div
          style={{
            position: 'fixed',
            left: contextMenu.x,
            bottom: `calc(70px + env(safe-area-inset-bottom))`,
            transform: 'translateX(-50%)',
            backgroundColor: colors.primaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '12px',
            boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.6)' : '0 8px 24px rgba(0,0,0,0.2)',
            overflow: 'hidden',
            zIndex: 10000,
            minWidth: '140px',
            fontFamily,
            animation: 'contextMenuIn 0.15s ease-out forwards',
          }}
          onClick={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <style>{`
            @keyframes contextMenuIn {
              from { opacity: 0; transform: translateX(-50%) scale(0.9) translateY(10px); }
              to { opacity: 1; transform: translateX(-50%) scale(1) translateY(0); }
            }
          `}</style>
          {(() => {
            const windowForType = windows.find(w => w.contentType === contextMenu.type)
            const isOpen = !!windowForType
            const navItem = navItems.find(n => n.type === contextMenu.type)
            return (
              <>
                <div style={{ 
                  padding: '8px 12px', 
                  fontSize: '10px', 
                  fontWeight: 600, 
                  color: colors.secondaryText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderBottom: `1px solid ${colors.primaryBorder}`,
                }}>
                  {navItem?.label}
                </div>
                {isOpen ? (
                  <>
                    <button
                      onClick={() => {
                        const idx = windows.findIndex(w => w.contentType === contextMenu.type)
                        if (idx !== -1) onWindowSelect(idx)
                        setContextMenu(null)
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: colors.primaryText,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('👁️')}</span> Focus
                    </button>
                    <button
                      onClick={() => {
                        if (windowForType) onCloseWindow(windowForType.id)
                        setContextMenu(null)
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: '#ef4444',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>✕</span> Close
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      onOpenWindow(contextMenu.type)
                      setContextMenu(null)
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: colors.primaryText,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('➕')}</span> Open
                  </button>
                )}
              </>
            )
          })()}
        </div>,
        document.body
      )}
      
      {/* App Switcher Overlay - Framer Motion animations */}
      {showAppSwitcher && createPortal(
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            zIndex: 999999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '20px',
            paddingTop: 'calc(60px + env(safe-area-inset-top))',
            fontFamily: "'Departure Mono', monospace",
          }}
          onClick={() => setShowAppSwitcher(false)}
        >
          <div 
            style={{ 
              textAlign: 'center', 
              marginBottom: '20px',
              color: colors.primaryText,
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            Open Windows ({windows.length})
          </div>
          <div 
            style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
              maxWidth: '400px',
              maxHeight: 'calc(100dvh - 200px)',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
              {windows.map((win, idx) => {
                const navItem = navItems.find(n => n.type === win.contentType)
                const isActive = idx === activeWindowIndex
                return (
                  <motion.div
                    key={win.id}
                    initial={{ opacity: 0, x: -25, scale: 0.97 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ 
                      duration: 0.35, 
                      delay: 0.08 + idx * 0.05,
                      ease: [0.32, 0.72, 0, 1]
                    }}
                    style={{
                      backgroundColor: isActive ? colors.primaryBg : colors.primaryAccent,
                      border: `1px solid ${isActive ? colors.primaryBorder : colors.primaryAccent}`,
                      borderRadius: '12px',
                      padding: '14px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                    onClick={() => {
                      onWindowSelect(idx)
                      setShowAppSwitcher(false)
                    }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <span style={{ 
                      fontSize: '22px', 
                      fontFamily: "'Noto Emoji', sans-serif",
                      fontWeight: 600,
                      WebkitTextStroke: '0.5px currentColor',
                      color: isActive ? colors.primaryText : colors.primaryBg,
                    }}>{navItem?.icon}</span>
                    <div style={{ 
                      flex: 1,
                      fontSize: '14px', 
                      fontWeight: 600, 
                      color: isActive ? colors.primaryText : colors.primaryBg,
                    }}>
                      {win.title}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onCloseWindow(win.id)
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        backgroundColor: isActive ? 'rgba(239, 68, 68, 0.15)' : 'rgba(0,0,0,0.2)',
                        border: 'none',
                        color: isActive ? '#ef4444' : colors.primaryBg,
                        fontSize: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ✕
                    </button>
                  </motion.div>
                )
              })}
            </div>
          {/* Back text at bottom left */}
          <div
            onClick={(e) => {
              e.stopPropagation()
              setShowAppSwitcher(false)
            }}
            style={{
              position: 'absolute',
              bottom: 'calc(24px + env(safe-area-inset-bottom))',
              left: '20px',
              color: colors.primaryText,
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              fontFamily: "'Departure Mono', monospace",
            }}
          >
            {'<'} Back
          </div>
        </motion.div>,
        document.body
      )}


      {/* Login Modal */}
      {showLoginModal && onLoginSuccess && onCloseLoginModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '16px',
            fontFamily,
          }}
          onClick={onCloseLoginModal}
        >
          <div
            style={{
              backgroundColor: colors.primaryBg,
              borderRadius: '12px',
              border: `1px solid ${colors.primaryBorder}`,
              maxWidth: '400px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <AuthWindow
              onAuthSuccess={onLoginSuccess}
              extraData={{ mode: 'login' }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Account Modal */}
      {showAccountModal && <AccountInfo onClose={() => setShowAccountModal(false)} />}
      
      {/* Subscription Modal */}
      {showSubscriptionModal && <Subscription onClose={() => setShowSubscriptionModal(false)} />}
      
      {/* Import ChatGPT Modal */}
      {showImportChatGPTModal && (
        <ImportChatGPT 
          onClose={() => setShowImportChatGPTModal(false)}
          onOpenSubscription={() => {
            setShowImportChatGPTModal(false)
            setShowSubscriptionModal(true)
          }}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '24px',
            fontFamily,
          }}
          onClick={() => setShowLogoutConfirm(false)}
        >
          <div
            style={{
              backgroundColor: colors.primaryBg,
              borderRadius: '12px',
              border: `1px solid ${colors.primaryBorder}`,
              padding: '24px',
              maxWidth: '320px',
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px', color: colors.primaryText }}>
              Confirm Logout
            </h3>
            <p style={{ fontSize: '12px', color: colors.secondaryText, marginBottom: '16px' }}>
              Are you sure you want to log out?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowLogoutConfirm(false)} variant="secondary" size="sm">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowLogoutConfirm(false)
                  handleLogout()
                }}
                variant="destructive"
                size="sm"
                disabled={isLoggingOut}
                isLoading={isLoggingOut}
              >
                Log Out
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

